import { useCallback, useEffect, useId, useRef } from 'react';
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import IconButton from './IconButton';

/**
 * Shared dialog primitive. Strict header / body / footer API:
 *   <Modal open onClose title>
 *     <Modal.Body>…</Modal.Body>
 *     <Modal.Footer><Button variant="secondary">Cancel</Button><Button>Save</Button></Modal.Footer>
 *   </Modal>
 *
 * Built-in:
 *   - role="dialog" + aria-modal + aria-labelledby (auto-wired to the title)
 *   - Escape closes
 *   - Backdrop click closes (unless `dismissOnBackdrop={false}`)
 *   - Body scroll lock while open
 *   - Focus moves into the dialog on open; previous focus is restored on close
 *   - Tab/Shift+Tab cycles inside the dialog (focus trap)
 *   - Rendered through a portal at document.body so stacking-context bugs don't leak
 */

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Optional secondary text below the title. */
  description?: ReactNode;
  /** Hide the visual title bar (still rendered as sr-only for a11y). */
  hideTitle?: boolean;
  size?: ModalSize;
  dismissOnBackdrop?: boolean;
  initialFocusRef?: React.RefObject<HTMLElement | null>;
  className?: string;
  children: ReactNode;
}

const SIZE_CLASSES: Record<ModalSize, string> = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-6xl',
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function Modal({
  open,
  onClose,
  title,
  description,
  hideTitle = false,
  size = 'md',
  dismissOnBackdrop = true,
  initialFocusRef,
  className = '',
  children,
}: ModalProps) {
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  // Lock body scroll while the dialog is open.
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Capture the previously-focused element on open; restore on close.
  useEffect(() => {
    if (!open) return;
    previouslyFocused.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Move focus into the dialog on the next paint so any initial-focus ref is mounted first.
    const id = window.requestAnimationFrame(() => {
      if (initialFocusRef?.current) {
        initialFocusRef.current.focus();
      } else if (dialogRef.current) {
        const firstFocusable = dialogRef.current.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        (firstFocusable ?? dialogRef.current).focus();
      }
    });
    return () => {
      window.cancelAnimationFrame(id);
      previouslyFocused.current?.focus?.();
    };
  }, [open, initialFocusRef]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent<Document> | globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler as EventListener);
    return () => document.removeEventListener('keydown', handler as EventListener);
  }, [open, onClose]);

  // Tab focus trap.
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
    );
    if (focusables.length === 0) {
      e.preventDefault();
      dialogRef.current.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const handleBackdropClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!dismissOnBackdrop) return;
      if (e.target === e.currentTarget) onClose();
    },
    [dismissOnBackdrop, onClose],
  );

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 dark:bg-black/70"
      onMouseDown={handleBackdropClick}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={`w-full ${SIZE_CLASSES[size]} max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 focus:outline-none ${className}`.trim()}
      >
        <header
          className={
            hideTitle
              ? 'sr-only'
              : 'flex items-start justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700'
          }
        >
          <div className="min-w-0 flex-1">
            <h2 id={titleId} className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
              {title}
            </h2>
            {description ? (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{description}</p>
            ) : null}
          </div>
          {!hideTitle ? (
            <IconButton aria-label="Close dialog" onClick={onClose} className="shrink-0">
              <X className="w-5 h-5" />
            </IconButton>
          ) : null}
        </header>
        {children}
      </div>
    </div>,
    document.body,
  );
}

interface ModalBodyProps {
  children: ReactNode;
  className?: string;
}

function ModalBody({ children, className = '' }: ModalBodyProps) {
  return <div className={`px-4 sm:px-6 py-4 overflow-y-auto flex-1 ${className}`.trim()}>{children}</div>;
}

interface ModalFooterProps {
  children: ReactNode;
  className?: string;
  /** When 'between', actions hug opposite ends; default 'end' right-aligns. */
  align?: 'start' | 'end' | 'between';
}

function ModalFooter({ children, className = '', align = 'end' }: ModalFooterProps) {
  const justify =
    align === 'between' ? 'justify-between' : align === 'start' ? 'justify-start' : 'justify-end';
  return (
    <footer
      className={`flex flex-wrap items-center gap-2 px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-lg ${justify} ${className}`.trim()}
    >
      {children}
    </footer>
  );
}

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
